import { ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import { Fragment, type ReactNode, useContext } from 'react';
import { InCardContext } from '~/components/ui/card/Card';
import { EmptyState } from '~/components/ui/empty-state';
import { LoadError } from '~/components/ui/load-error';
import { ClickableCell, ClickableRow, RowAnchor } from '~/components/ui/table';
import { TableRowsSkeleton } from '~/components/ui/table/TableRowsSkeleton';
import {
  TABLE_BODY,
  TABLE_DIVIDER,
  TABLE_FRAME,
  TABLE_HEAD_ROW,
  TABLE_PINNED_ACTIONS,
  TABLE_ROW,
  TABLE_ROW_HOVER,
  TABLE_SELECT_COL,
  TABLE_TD,
  tablePinnedStart,
  TABLE_TH,
  hasDivider,
} from '~/components/ui/table/tokens';
import { SelectAllCheckbox } from '~/components/ui/datatable/SelectAllCheckbox';

const SELECT_ID = 'select';

export interface DataTableColumn<T> {
  /** The column with id `actions` gets the centred, `w-28` actions treatment. */
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  sortable?: boolean;
  /** Let prose wrap. Cells are one line by default (the frame scrolls sideways). */
  wrap?: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  /** First load: the real header over skeleton rows. */
  loading?: boolean;
  /** A refetch while rows show: they stay and a thin progress line runs above them. */
  refreshing?: boolean;
  /** Required: what is empty, and what to do about it (sentence case; "No feeds yet." / "Add a feed to start importing listings."). */
  emptyMessage: string;
  emptyDescription: string;
  /** Defaults to framed outside a `Card` and unframed inside one; a card's table never has an outer border. */
  framed?: boolean;
  /** A persistent load failure, rendered as the shared `LoadError` in place of the rows. */
  error?: { what: string; message?: string; onRetry?: () => void };
  /** Where a click on the row (not the actions cell) navigates, or null for an inert row. */
  href?: (row: T) => string | null;
  /** A click on the row runs this in place. Ignored when `href` returns a string for the row. */
  onRowClick?: (row: T) => void;
  /** Accessible name for a clickable row and its selection checkbox. */
  rowLabel?: (row: T) => string;
  /** Controlled sort: the active column id, its direction, and the header click. */
  sort?: string;
  order?: 'asc' | 'desc';
  onSort?: (id: string) => void;
  /** Selection adds a leading checkbox column. Supply all three. */
  selectedKeys?: Set<string>;
  onToggleRow?: (key: string) => void;
  onToggleAll?: (checked: boolean) => void;
  testId?: string;
}

// The pinned cells are opaque, so they repeat the row's hover tint themselves (surface-raised = the dark tint flattened onto gray-900).
const PINNED_HOVER = 'transition-colors group-hover:bg-gray-50 dark:group-hover:bg-surface-raised';

/** The pinned first column draws its line as an ::after, so it never also takes the grid divider. */
const th = (id: string, divider: boolean, pinStart: string) =>
  `${TABLE_TH} ${id === 'actions' ? `text-center w-28 ${TABLE_PINNED_ACTIONS}` : 'text-left'}${pinStart ? ` ${pinStart}` : divider ? ` ${TABLE_DIVIDER}` : ''}`;

export function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  refreshing = false,
  emptyMessage,
  emptyDescription,
  error,
  framed: framedProp,
  href,
  onRowClick,
  rowLabel,
  sort,
  order,
  onSort,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  testId,
}: DataTableProps<T>) {
  const inCard = useContext(InCardContext);
  const framed = framedProp ?? !inCard;
  const selectable =
    selectedKeys !== undefined && onToggleRow !== undefined && onToggleAll !== undefined;
  const id = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
  const columnIds = columns.map((c) => c.id);
  // The first column pins left (after the checkbox column, which pins too) unless it is the actions column.
  const pinFirst = columns.length > 0 && columns[0].id !== 'actions';
  const firstPin = pinFirst ? tablePinnedStart(selectable ? 'left-16' : 'left-0') : '';
  const selectPin = tablePinnedStart('left-0');
  const frame = framed ? TABLE_FRAME : 'relative';

  if (error) {
    return (
      <div className="w-full">
        <LoadError {...error} testId={id('error')} />
      </div>
    );
  }

  const selectHeader = selectable ? (
    <th key={SELECT_ID} className={`${TABLE_TH} ${TABLE_SELECT_COL} text-left ${selectPin}`}>
      <SelectAllCheckbox
        keys={data.map(rowKey)}
        selected={selectedKeys}
        onChange={onToggleAll}
        label="Select all rows"
        testId={id('select-all')}
      />
    </th>
  ) : null;

  if (loading) {
    return (
      <TableRowsSkeleton
        testId={id('loading')}
        pinActions
        framed={framed}
        columns={[
          ...(selectable ? [{ id: SELECT_ID, header: '', className: TABLE_SELECT_COL }] : []),
          ...columns.map((column) => ({ id: column.id, header: column.header })),
        ]}
      />
    );
  }

  if (data.length === 0) {
    return (
      <div className="w-full">
        <div className={frame} data-testid={id('empty')}>
          <EmptyState
            icon={<Inbox className="size-8" aria-hidden="true" />}
            title={emptyMessage}
            description={emptyDescription}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={frame}>
        {refreshing && (
          <div
            role="progressbar"
            aria-label="Refreshing"
            className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden"
            data-testid={id('refreshing')}
          >
            <div className="h-full w-2/5 animate-progress bg-brand-500 motion-reduce:animate-none" />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full" data-testid={testId}>
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                {selectHeader}
                {columns.map((column, index) => {
                  const actions = column.id === 'actions';
                  const canSort = Boolean(column.sortable && onSort);
                  const active = sort === column.id;
                  const ariaSort = canSort
                    ? active
                      ? order === 'desc'
                        ? 'descending'
                        : 'ascending'
                      : 'none'
                    : undefined;
                  return (
                    <th
                      key={column.id}
                      aria-sort={ariaSort}
                      className={th(
                        column.id,
                        hasDivider(columnIds, index),
                        index === 0 ? firstPin : '',
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={() => onSort?.(column.id)}
                          className={`cursor-pointer select-none flex items-center gap-2 bg-transparent border-0 p-0 uppercase tracking-wider ${actions ? 'justify-center w-full' : ''}`}
                          data-testid={id(`header-${column.id}`)}
                        >
                          {column.header}
                          <span className="inline-flex flex-col text-gray-400" aria-hidden="true">
                            <ChevronUp
                              className={`size-3 -mb-1 ${active && order === 'asc' ? 'text-gray-700 dark:text-gray-300' : ''}`}
                            />
                            <ChevronDown
                              className={`size-3 ${active && order === 'desc' ? 'text-gray-700 dark:text-gray-300' : ''}`}
                            />
                          </span>
                        </button>
                      ) : (
                        <div className={actions ? 'flex justify-center' : ''}>{column.header}</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className={TABLE_BODY}>
              {data.map((row) => {
                const key = rowKey(row);
                const target = href?.(row) ?? null;
                const activate = target === null && onRowClick ? () => onRowClick(row) : undefined;
                const interactive = target !== null || activate !== undefined;
                const selected = selectedKeys?.has(key) ?? false;
                const label = rowLabel?.(row);
                const rowClass = `group ${TABLE_ROW} ${interactive ? TABLE_ROW_HOVER : ''}`;
                let linkPlaced = false;
                const cells: ReactNode[] = [];
                if (selectable) {
                  cells.push(
                    <td
                      key={SELECT_ID}
                      className={`${TABLE_TD} ${TABLE_SELECT_COL} ${selectPin} ${interactive ? PINNED_HOVER : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="size-4 rounded border-gray-300 accent-brand-500 dark:border-gray-700"
                        checked={selected}
                        onChange={() => onToggleRow(key)}
                        aria-label={label ? `Select ${label}` : 'Select row'}
                      />
                    </td>,
                  );
                }
                columns.forEach((column, index) => {
                  const actions = column.id === 'actions';
                  const clickable = interactive && !actions;
                  const pinStart = index === 0 ? firstPin : '';
                  const pinned =
                    actions || pinStart
                      ? `${actions ? TABLE_PINNED_ACTIONS : pinStart} ${interactive ? PINNED_HOVER : ''}`
                      : '';
                  const cellClass =
                    `${TABLE_TD} ${column.wrap ? '' : 'whitespace-nowrap'} ${actions ? 'text-center' : ''} ${clickable ? 'cursor-pointer' : ''} ${
                      !pinStart && hasDivider(columnIds, index) ? TABLE_DIVIDER : ''
                    } ${pinned} ${column.className ?? ''}`
                      .replace(/\s+/g, ' ')
                      .trim();
                  let content = column.cell(row);
                  if (clickable && target !== null && !linkPlaced) {
                    linkPlaced = true;
                    content = <RowAnchor href={target}>{content}</RowAnchor>;
                  }
                  if (!clickable) {
                    cells.push(
                      <td key={column.id} className={cellClass}>
                        {content}
                      </td>,
                    );
                  } else if (target !== null) {
                    cells.push(
                      <ClickableCell key={column.id} href={target} className={cellClass}>
                        {content}
                      </ClickableCell>,
                    );
                  } else if (activate) {
                    cells.push(
                      <ClickableCell key={column.id} onActivate={activate} className={cellClass}>
                        {content}
                      </ClickableCell>,
                    );
                  }
                });
                const dataState = selected ? 'selected' : false;
                return (
                  <Fragment key={key}>
                    {target !== null ? (
                      <ClickableRow
                        href={target}
                        rowLabel={label}
                        className={rowClass}
                        dataState={dataState}
                        testId={id('row')}
                        rowId={testId ? key : undefined}
                      >
                        {cells}
                      </ClickableRow>
                    ) : activate ? (
                      <ClickableRow
                        onActivate={activate}
                        rowLabel={label}
                        className={rowClass}
                        dataState={dataState}
                        testId={id('row')}
                        rowId={testId ? key : undefined}
                      >
                        {cells}
                      </ClickableRow>
                    ) : (
                      <tr
                        className={rowClass}
                        data-state={dataState || undefined}
                        data-testid={id('row')}
                        data-row-id={testId ? key : undefined}
                      >
                        {cells}
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
